import React, { useState } from "react";
import { Col, ListGroup, ListGroupItem, Row } from "reactstrap";

import { titalize } from "@common/lib/titalize";
import { Dispatch, State, TaxonomyMode } from "../StateManager/state";
import ColorPicker from "./ColorPicker";
import { Color } from "../helpers";
import "./LabelList.scss"

type Props = Readonly<{
  activeLabel: State["currentLabel"];
  currentLabelGroup: State["currentLabelGroup"];
  zoningTaxonomyName: string;
  dispatch: Dispatch;
  readonly: boolean;
  taxonomy: State["taxonomy"];
}>;

export const LabelList = ({
  activeLabel,
  currentLabelGroup,
  zoningTaxonomyName,
  dispatch,
  readonly,
  taxonomy,
}: Props) => {
  const showHeaders = taxonomy.mode === TaxonomyMode.Complex;  
  const [storageSaveTimeout, setStorageSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [groupCollapsed, setGroupCollapsed] = useState<{}>({});
  return (
    <div className="label-list">
      <Row>
        <Col>
          <ListGroup className={showHeaders ? "indented" : ""}>
            {Object.keys(taxonomy.orderedLabels).map((key, groupIndex) => {

              const labelList: Array<string> | undefined =
              taxonomy.orderedLabels[key as keyof State["taxonomy"]["orderedLabels"]];
              if (!labelList) {
                return null;
              }
              const labelGroupClasses = ["label-group"];
              if (currentLabelGroup === key) {
                labelGroupClasses.push("active-label-group");
              }
              if (key === "starred" && labelList.length > 0) {
                labelGroupClasses.push("starred-group");
              }
              return (
                <div className={labelGroupClasses.join(" ")} key={key}>
                  {(showHeaders || (key === "starred" && labelList.length > 0)) && (
                    <ListGroupItem 
                      onClick={(e) => {
                        let newCollapsedState = {...groupCollapsed}
                        if (!!newCollapsedState[key]) {
                          delete newCollapsedState[key];
                        } else {
                          newCollapsedState[key] = true;
                        }
                        setGroupCollapsed(newCollapsedState)
                      }}
                      className="header"
                    >
                      {titalize(key)}
                    </ListGroupItem>
                  )}
                  {!groupCollapsed[key] && labelList.map((label, labelIndex) => {
                    let hotKey = "";
                    if (groupIndex < 9 && labelIndex < 9) {
                      if (showHeaders) {
                        hotKey = `${groupIndex + 1} + ${labelIndex + 1}`;
                      } else {
                        hotKey = `${groupIndex + 1}`;
                      }
                    }
                    const title = titalize(label);
                    return (
                      <ListGroupItem
                        active={activeLabel === label}
                        className={`label-row align-items-center d-flex${readonly ? "" : " selectable"}`}
                        key={label}
                        onClick={() => {
                          if (!readonly) {
                            dispatch({
                              label,
                              type: "CHANGE CURRENT LABEL",
                            });
                          }
                        }}
                        title={title}
                      >
                        <ColorPicker 
                          color={taxonomy.rawLabels[label].color}
                          onChange={(e) => {
                            dispatch({
                                labelKey: label,
                                zoningLabel: taxonomy.rawLabels[label],
                                color: e,
                                type: "CHANGE LABEL COLOR"
                            })
                            if (storageSaveTimeout) {
                              clearTimeout(storageSaveTimeout);
                            }
                            const newTaxi = {
                                ...taxonomy.rawLabels,
                                [label]: {
                                  group: taxonomy.rawLabels[label].group,
                                  color: new Color(
                                    e.rgb.r, 
                                    e.rgb.g,
                                    e.rgb.b,
                                    e.rgb.a)
                                }
                            }
                            
                            // Prevent chain-saving to local storage while user (not sure if this is necessary)
                            setStorageSaveTimeout(setTimeout(() => {
                              localStorage.setItem("user_taxi_colors_" + zoningTaxonomyName, JSON.stringify(newTaxi));
                          }, 200));
                          }}
                        ></ColorPicker>
                        <div className="flex-fill text-truncate">{title}</div>
                        {hotKey && !readonly && <kbd>{hotKey}</kbd>}
                        {(!!taxonomy.orderedLabels.starred?.find(x => x === label) === (key === 'starred')) && <div
                          className="star-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch({
                              taxonomyName:zoningTaxonomyName,
                              label: label,
                              type:"STAR LABEL"
                            });
                          }}
                        >★</div>}
                      </ListGroupItem>
                    );
                  })}
                </div>
              );
            })}
          </ListGroup>
        </Col>
      </Row>
    </div>
  );
};
LabelList.displayName = "LabelList";
